import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSettingsPage = ({ user, token }) => {
  const [hubs, setHubs] = useState([]);
  const [materialTypes, setMaterialTypes] = useState([]);
  const [vehicleNumbers, setVehicleNumbers] = useState([]);
  const [staffNames, setStaffNames] = useState([]);
  
  const [isHubDialogOpen, setIsHubDialogOpen] = useState(false);
  const [isMaterialDialogOpen, setIsMaterialDialogOpen] = useState(false);
  const [isVehicleDialogOpen, setIsVehicleDialogOpen] = useState(false);
  const [isStaffNameDialogOpen, setIsStaffNameDialogOpen] = useState(false);
  
  const [deleteHubId, setDeleteHubId] = useState(null);
  const [deleteMaterialId, setDeleteMaterialId] = useState(null);
  const [deleteVehicleId, setDeleteVehicleId] = useState(null);
  const [deleteStaffNameId, setDeleteStaffNameId] = useState(null);
  
  const [hubForm, setHubForm] = useState({ name: "", location: "" });
  const [materialForm, setMaterialForm] = useState({ name: "", category: "" });
  const [vehicleForm, setVehicleForm] = useState({ vehicle_number: "" });
  const [staffNameForm, setStaffNameForm] = useState({ name: "" });
  
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user?.role !== 'admin') return;
    fetchHubs();
    fetchMaterialTypes();
    fetchVehicleNumbers();
    fetchStaffNames();
  }, []);

  const fetchHubs = async () => {
    try {
      const response = await axios.get(`${API}/hubs`, { headers: { Authorization: `Bearer ${token}` } });
      setHubs(response.data);
    } catch (error) {
      console.error("Error fetching hubs:", error);
    }
  };

  const fetchMaterialTypes = async () => {
    try {
      const response = await axios.get(`${API}/material-types`, { headers: { Authorization: `Bearer ${token}` } });
      setMaterialTypes(response.data);
    } catch (error) {
      console.error("Error fetching material types:", error);
    }
  };

  const fetchVehicleNumbers = async () => {
    try {
      const response = await axios.get(`${API}/vehicle-numbers`, { headers: { Authorization: `Bearer ${token}` } });
      setVehicleNumbers(response.data);
    } catch (error) {
      console.error("Error fetching vehicle numbers:", error);
    }
  };

  const fetchStaffNames = async () => {
    try {
      const response = await axios.get(`${API}/staff-names`, { headers: { Authorization: `Bearer ${token}` } });
      setStaffNames(response.data);
    } catch (error) {
      console.error("Error fetching staff names:", error);
    }
  };

  const handleCreateHub = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/hubs`, hubForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Hub created successfully!");
      setHubForm({ name: "", location: "" });
      setIsHubDialogOpen(false);
      fetchHubs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create hub");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteHub = async () => {
    try {
      await axios.delete(`${API}/hubs/${deleteHubId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Hub deleted successfully!");
      setDeleteHubId(null);
      fetchHubs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete hub");
    }
  };

  const handleCreateMaterial = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/material-types`, materialForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Material type created successfully!");
      setMaterialForm({ name: "", category: "" });
      setIsMaterialDialogOpen(false);
      fetchMaterialTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create material type");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMaterial = async () => {
    try {
      await axios.delete(`${API}/material-types/${deleteMaterialId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Material type deleted successfully!");
      setDeleteMaterialId(null);
      fetchMaterialTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete material type");
    }
  };

  const handleCreateVehicle = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/vehicle-numbers`, vehicleForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Vehicle number added successfully!");
      setVehicleForm({ vehicle_number: "" });
      setIsVehicleDialogOpen(false);
      fetchVehicleNumbers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add vehicle number");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteVehicle = async () => {
    try {
      await axios.delete(`${API}/vehicle-numbers/${deleteVehicleId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Vehicle number deleted successfully!");
      setDeleteVehicleId(null);
      fetchVehicleNumbers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete vehicle number");
    }
  };

  const handleCreateStaffName = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/staff-names`, staffNameForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Staff name added successfully!");
      setStaffNameForm({ name: "" });
      setIsStaffNameDialogOpen(false);
      fetchStaffNames();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add staff name");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteStaffName = async () => {
    try {
      await axios.delete(`${API}/staff-names/${deleteStaffNameId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Staff name deleted successfully!");
      setDeleteStaffNameId(null);
      fetchStaffNames();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete staff name");
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="p-6 md:p-8 lg:p-12 space-y-8" data-testid="admin-settings-page">
        <Card className="shadow-lg border-border/50">
          <CardContent className="flex flex-col items-center justify-center p-12 text-muted-foreground">
            <p className="text-lg">Access Denied. Admin privileges required.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 lg:p-12 space-y-8" data-testid="admin-settings-page">
      <div>
        <h1 className="text-4xl md:text-5xl font-bold font-heading tracking-tight">Admin Settings</h1>
        <p className="text-muted-foreground mt-2">Manage hubs, materials, vehicles, and staff</p>
      </div>

      <Tabs defaultValue="hubs" className="w-full">
        <TabsList className="grid w-full max-w-2xl grid-cols-4 bg-gradient-to-r from-green-50 via-white to-blue-50 p-1 rounded-xl border border-border">
          <TabsTrigger value="hubs" data-testid="hubs-tab" className="rounded-lg data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary data-[state=active]:to-secondary data-[state=active]:text-white data-[state=active]:shadow-md">Hubs</TabsTrigger>
          <TabsTrigger value="materials" data-testid="materials-tab" className="rounded-lg data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary data-[state=active]:to-secondary data-[state=active]:text-white data-[state=active]:shadow-md">Materials</TabsTrigger>
          <TabsTrigger value="vehicles" data-testid="vehicles-tab" className="rounded-lg data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary data-[state=active]:to-secondary data-[state=active]:text-white data-[state=active]:shadow-md">Vehicles</TabsTrigger>
          <TabsTrigger value="staff-names" data-testid="staff-names-tab" className="rounded-lg data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary data-[state=active]:to-secondary data-[state=active]:text-white data-[state=active]:shadow-md">Staff Names</TabsTrigger>
        </TabsList>

        {/* HUBS TAB */}
        <TabsContent value="hubs" className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-heading">Manage Hubs</h2>
            <Dialog open={isHubDialogOpen} onOpenChange={setIsHubDialogOpen}>
              <DialogTrigger asChild>
                <Button className="gap-2" data-testid="add-hub-button">
                  <Plus className="w-4 h-4" />
                  Add Hub
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="text-2xl font-heading">Add New Hub</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateHub} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="hub-name">Hub Name</Label>
                    <Input
                      id="hub-name"
                      data-testid="hub-name-input"
                      type="text"
                      placeholder="Enter hub name"
                      value={hubForm.name}
                      onChange={(e) => setHubForm({ ...hubForm, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="hub-location">Location</Label>
                    <Input
                      id="hub-location"
                      data-testid="hub-location-input"
                      type="text"
                      placeholder="Enter location"
                      value={hubForm.location}
                      onChange={(e) => setHubForm({ ...hubForm, location: e.target.value })}
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading} data-testid="submit-hub-button">
                    {loading ? "Creating..." : "Create Hub"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <Card className="shadow-lg border-border/50">
            <CardContent className="pt-6">
              <Table data-testid="hubs-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Hub Name</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {hubs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-muted-foreground">
                        No hubs found
                      </TableCell>
                    </TableRow>
                  ) : (
                    hubs.map((hub) => (
                      <TableRow key={hub.id}>
                        <TableCell className="font-medium">{hub.name}</TableCell>
                        <TableCell>{hub.location}</TableCell>
                        <TableCell>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                            {hub.active ? 'Active' : 'Inactive'}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => setDeleteHubId(hub.id)}
                            data-testid={`delete-hub-${hub.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* MATERIALS TAB */}
        <TabsContent value="materials" className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-heading">Manage Material Types</h2>
            <Dialog open={isMaterialDialogOpen} onOpenChange={setIsMaterialDialogOpen}>
              <DialogTrigger asChild>
                <Button className="gap-2" data-testid="add-material-button">
                  <Plus className="w-4 h-4" />
                  Add Material Type
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="text-2xl font-heading">Add Material Type</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateMaterial} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="material-name">Material Name</Label>
                    <Input
                      id="material-name"
                      data-testid="material-name-input"
                      type="text"
                      placeholder="e.g., PET Bottles"
                      value={materialForm.name}
                      onChange={(e) => setMaterialForm({ ...materialForm, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="material-category">Category</Label>
                    <Input
                      id="material-category"
                      data-testid="material-category-input"
                      type="text"
                      placeholder="e.g., Plastic"
                      value={materialForm.category}
                      onChange={(e) => setMaterialForm({ ...materialForm, category: e.target.value })}
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading} data-testid="submit-material-button">
                    {loading ? "Creating..." : "Create Material Type"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <Card className="shadow-lg border-border/50">
            <CardContent className="pt-6">
              <Table data-testid="materials-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Material Name</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {materialTypes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center text-muted-foreground">
                        No material types found
                      </TableCell>
                    </TableRow>
                  ) : (
                    materialTypes.map((material) => (
                      <TableRow key={material.id}>
                        <TableCell className="font-medium">{material.name}</TableCell>
                        <TableCell>{material.category}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => setDeleteMaterialId(material.id)}
                            data-testid={`delete-material-${material.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* VEHICLES TAB */}
        <TabsContent value="vehicles" className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-heading">Manage Vehicle Numbers</h2>
            <Dialog open={isVehicleDialogOpen} onOpenChange={setIsVehicleDialogOpen}>
              <DialogTrigger asChild>
                <Button className="gap-2" data-testid="add-vehicle-button">
                  <Plus className="w-4 h-4" />
                  Add Vehicle Number
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="text-2xl font-heading">Add Vehicle Number</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateVehicle} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="vehicle-number">Vehicle Number</Label>
                    <Input
                      id="vehicle-number"
                      data-testid="vehicle-number-input"
                      type="text"
                      placeholder="e.g., MH-12-AB-1234"
                      value={vehicleForm.vehicle_number}
                      onChange={(e) => setVehicleForm({ vehicle_number: e.target.value })}
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading} data-testid="submit-vehicle-button">
                    {loading ? "Adding..." : "Add Vehicle Number"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <Card className="shadow-lg border-border/50">
            <CardContent className="pt-6">
              <Table data-testid="vehicles-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Vehicle Number</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {vehicleNumbers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={2} className="text-center text-muted-foreground">
                        No vehicle numbers found
                      </TableCell>
                    </TableRow>
                  ) : (
                    vehicleNumbers.map((vehicle) => (
                      <TableRow key={vehicle.id}>
                        <TableCell className="font-medium">{vehicle.vehicle_number}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => setDeleteVehicleId(vehicle.id)}
                            data-testid={`delete-vehicle-${vehicle.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* STAFF NAMES TAB */}
        <TabsContent value="staff-names" className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-heading">Manage Staff Names</h2>
            <Dialog open={isStaffNameDialogOpen} onOpenChange={setIsStaffNameDialogOpen}>
              <DialogTrigger asChild>
                <Button className="gap-2" data-testid="add-staff-name-button">
                  <Plus className="w-4 h-4" />
                  Add Staff Name
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="text-2xl font-heading">Add Staff Name</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateStaffName} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="staff-name">Staff Name</Label>
                    <Input
                      id="staff-name"
                      data-testid="staff-name-input"
                      type="text"
                      placeholder="Enter staff name"
                      value={staffNameForm.name}
                      onChange={(e) => setStaffNameForm({ name: e.target.value })}
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading} data-testid="submit-staff-name-button">
                    {loading ? "Adding..." : "Add Staff Name"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <Card className="shadow-lg border-border/50">
            <CardContent className="pt-6">
              <Table data-testid="staff-names-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Staff Name</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {staffNames.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={2} className="text-center text-muted-foreground">
                        No staff names found
                      </TableCell>
                    </TableRow>
                  ) : (
                    staffNames.map((staff) => (
                      <TableRow key={staff.id}>
                        <TableCell className="font-medium">{staff.name}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => setDeleteStaffNameId(staff.id)}
                            data-testid={`delete-staff-name-${staff.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Alert Dialogs */}
      <AlertDialog open={!!deleteHubId} onOpenChange={() => setDeleteHubId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Hub?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the hub.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteHub} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteMaterialId} onOpenChange={() => setDeleteMaterialId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Material Type?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the material type.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteMaterial} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteVehicleId} onOpenChange={() => setDeleteVehicleId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Vehicle Number?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the vehicle number.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteVehicle} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteStaffNameId} onOpenChange={() => setDeleteStaffNameId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Staff Name?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the staff name.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteStaffName} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminSettingsPage;
